import { OptionView } from '@Selectors/builder/myArmyList/make/unit/edit/OptionView';
import {
  ArmyListUnit,
  ArmyListUnitOption,
  Unit,
  UnitOptionAvailabilityAction,
  UnitOptionUsePoint,
} from '@Models';
import { injectable } from 'inversify';
import { v4 as uuidv4 } from 'uuid';

@injectable()
export class ArmyListUnitUnitOptionAction {
  public removeArmyListUnitUnitOption(
    armyListUnitOptions: ArmyListUnitOption[],
    optionId: number,
  ): ArmyListUnitOption[] {
    if (
      armyListUnitOptions.some(
        (item) =>
          item.unit_option_id === optionId && item.id != null && item.id !== '',
      )
    )
      return armyListUnitOptions.map(
        (item) =>
          ({
            ...item,
            isDeleted: item.isDeleted || item.unit_option_id === optionId,
          }) satisfies ArmyListUnitOption as ArmyListUnitOption,
      );

    return armyListUnitOptions.filter(
      (item) => item.unit_option_id !== optionId,
    );
  }

  public addArmyListUnitUnitOptions = (
    armyListUnitOptions: ArmyListUnitOption[],
    unitOptionId: number,
    name: string,
    quantity: number,
    points: number,
  ): ArmyListUnitOption[] => {
    if (
      armyListUnitOptions.find(
        (item) => item.unit_option_id === unitOptionId,
      ) != null
    )
      return armyListUnitOptions.map((item) => {
        if (item.unit_option_id === unitOptionId) {
          return {
            ...item,
            isDeleted: false,
            quantity: quantity,
            points: points,
          };
        }
        return item;
      });

    return armyListUnitOptions.concat([
      {
        id: uuidv4(),
        name: name,
        quantity: quantity,
        unit_option_id: unitOptionId,
        points: points,
        isDeleted: false,
      } satisfies ArmyListUnitOption as ArmyListUnitOption,
    ]);
  };

  public updateArmyListUnitUnitOptions = (
    armyListUnit: ArmyListUnit,
    unit: Unit,
    optioId: number,
  ): ArmyListUnitOption[] => {
    let armyListUnitOptions = armyListUnit.army_list_unit_unit_options;

    const modifiers =
      unit.optionGroups.flatMap((o) => o.options).find((o) => o.id === optioId)
        ?.availabilityModifiers ?? [];
    if (modifiers.length > 0) {
      // Mise à jour des prix pour points d'une autre option quand l'option est sélectionnée
      modifiers
        .filter((o) => o.action === UnitOptionAvailabilityAction.ChangePoints)
        .forEach((o) => {
          armyListUnitOptions = armyListUnitOptions.map((item) => {
            if (item.unit_option_id === o.unitOptionId) {
              const option = unit.optionGroups
                .flatMap((o) => o.options)
                .find((opt) => opt.id === o.unitOptionId);
              if (!option) return item;

              if (option.use_points === UnitOptionUsePoint.Cost) {
                return {
                  ...item,
                  points: item.quantity * option.points!!,
                };
              }

              return {
                ...item,
                points: 0,
              };
            }
            return item;
          });
        });
    }

    return this.removeArmyListUnitUnitOption(armyListUnitOptions, optioId);
  };

  public updateArmyListUnitUnitOptionQuantity = (
    armyListUnitUnitOptions: ArmyListUnitOption[],
    option: OptionView,
    quantity: number,
  ): ArmyListUnitOption[] => {
    if (
      !armyListUnitUnitOptions.find((item) => item.unit_option_id === option.id)
    )
      throw Error('Option to update not found');

    return armyListUnitUnitOptions.map((item) => {
      if (item.unit_option_id === option.id) {
        return {
          ...item,
          quantity: quantity,
          points: option.points!!.source * quantity,
        } satisfies ArmyListUnitOption as ArmyListUnitOption;
      }
      return item;
    });
  };
}
